import React, { useEffect } from 'react';
import { Table, Button, Popconfirm, Space } from 'antd';
import { useRole } from '../../store/roleContext';
import { Role } from '../../types/roles';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { translatePermissionWithGroup } from '../../utils/permissionTranslator';
import { useAuth } from '../../store/AuthContext';
import PageNavigation from '../Layout/PageNavigation';

interface RoleListProps {
    onEdit: (role: Role) => void;
}

const RoleList: React.FC<RoleListProps> = ({ onEdit }) => {
    const { roles, deleteRole, listRoles } = useRole();
    const { t, i18n } = useTranslation();
    const { hasPermission } = useAuth();

    useEffect(() => {
        listRoles();
        // eslint-disable-next-line
    }, []);

    const columns = [
        {
            title: t('name'),
            dataIndex: 'name',
        },
        {
            title: t('permissions_'),
            dataIndex: 'permissions',
            render: (permissions: any[]) => (
                <div style={{ whiteSpace: 'pre-line' }}>
                    {permissions && permissions.length
                        ? permissions.map((p, idx) => (
                            <span key={p.name || p}>
                                {translatePermissionWithGroup(p.name || p, t)}
                                {idx !== permissions.length - 1 ? ', ' : ''}
                            </span>
                        ))
                        : '-'}
                </div>
            ),
        },
        {
            title: t('actions'),
            render: (_: any, record: Role) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent:
                            i18n.language === 'ar' || i18n.language === 'ckb'
                                ? 'flex-end'
                                : 'flex-start',
                    }}
                >
                    <Space>
                        {hasPermission('role.update') && (
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() =>
                                    onEdit({
                                        ...record,
                                        permissions: record.permissions?.map((p: any) => p.id) || [],
                                    })
                                }
                            >
                                {t('edit')}
                            </Button>
                        )}
                        {hasPermission('role.delete') && (
                            <Popconfirm
                                title={t('sure_to_delete') || ''}
                                onConfirm={() => record.id && deleteRole(record.id)}
                            >
                                <Button type="primary" danger icon={<DeleteOutlined />}>
                                    {t('delete')}
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                </div>
            ),
        },
    ];

    return (
        <div
            style={{
                overflowX: 'auto',
                direction: i18n.language === 'ar' || i18n.language === 'ckb' ? 'rtl' : 'ltr',
            }}
        >
            <PageNavigation title={t('roles') as string} />
            <Table
                rowKey="id"
                columns={columns}
                dataSource={roles}
                pagination={{ pageSize: 6 }}
                scroll={{ x: 'max-content' }}
                size="middle"
            />
        </div>
    );
};

export default RoleList;
